import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import { exportAuthenticatedCartToGuestBag, persistGuestBagFromApiExport } from '../utils/cartGuestBridge';
import { mergeGuestCartToApi, guestBagReactSnapshotRef } from '../utils/mergeGuestCartToApi';
import { getApiUrl } from '../config/environment';
import { isAdminPanelPath } from '../constants/adminPanelRoutes';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [sessionExpiredShown, setSessionExpiredShown] = useState(false);

  // Load user from localStorage and validate token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        const savedUser = localStorage.getItem('sheenwear_user');
        const token = localStorage.getItem('authToken');
        
        if (savedUser && token) {
          try {
            const userData = JSON.parse(savedUser);
            
            // Validate token with backend before setting user
            const isValid = await checkTokenValidity();
            
            if (isValid) {
              // ✅ NEW: Handle addresses array when loading from localStorage and phone number mapping
              const mappedUser = {
                ...userData,
                // Map addresses array back to simple fields for form compatibility
                address: userData.addresses?.[0]?.addressLine1 || '',
                city: userData.addresses?.[0]?.city || '',
                country: userData.addresses?.[0]?.country || '',
                // ✅ CORRECT: Use 'phone' field from backend
                phone: userData.phone || ''
              };
              
              setUser(mappedUser);
              setIsAuthenticated(true);
            } else {
              // Token is invalid, clear auth data
              localStorage.removeItem('sheenwear_user');
              localStorage.removeItem('authToken');
              setUser(null);
              setIsAuthenticated(false);
            }
          } catch (error) {
            console.error('Error loading user from localStorage:', error);
            localStorage.removeItem('sheenwear_user');
            localStorage.removeItem('authToken');
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear invalid auth data
        localStorage.removeItem('sheenwear_user');
        localStorage.removeItem('authToken');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Check token validity with backend
  const checkTokenValidity = async () => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      return false;
    }

    try {
      const response = await authService.getCurrentUser();
      
      if (response.success && response.user) {
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token validation error:', error);
      
      // Check if it's a token-related error
      if (error.message) {
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.error && (
            errorData.error === 'Token expired. Please login again.' ||
            errorData.error === 'Invalid token' ||
            errorData.error === 'Not authorized to access this route'
          )) {
            // Clear invalid token
            localStorage.removeItem('authToken');
            localStorage.removeItem('sheenwear_user');
            return false;
          }
        } catch (parseError) {
          // If not JSON, check if it's a token error
          if (error.message.includes('Token expired') || 
              error.message.includes('Invalid token') ||
              error.message.includes('Not authorized')) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('sheenwear_user');
            return false;
          }
        }
      }
      
      // Don't force logout on transient/network errors.
      // Keep user logged in until explicit token-expired flow triggers.
      return true;
    }
  };

  // Listen for auth expired events with debounce
  useEffect(() => {
    const handleAuthExpired = () => {
      // Don't handle auth expired event on admin pages - let admin handle its own auth
      if (isAdminPanelPath(window.location.pathname)) {
        console.log('🔒 Admin page detected - ignoring authExpired event');
        return;
      }
      
      // Prevent multiple session expired messages
      if (sessionExpiredShown) {
        return;
      }
      
      setSessionExpiredShown(true);
      logout();
      
      if (window.showToast) {
        window.showToast('Session expired. Please login again.', 'error');
      }
      
      // Reset the flag after 5 seconds to allow future messages
      setTimeout(() => {
        setSessionExpiredShown(false);
      }, 5000);
    };

    window.addEventListener('authExpired', handleAuthExpired);
    
    return () => {
      window.removeEventListener('authExpired', handleAuthExpired);
    };
  }, [sessionExpiredShown]);

  const login = async (email, password) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await authService.login({ email, password });
      
      if (response.success && response.user) {
        // ✅ NEW: Handle addresses array in response and phone number mapping
        const mappedUser = {
          ...response.user,
          // Map addresses array back to simple fields for form compatibility
          address: response.user.addresses?.[0]?.addressLine1 || '',
          city: response.user.addresses?.[0]?.city || '',
          country: response.user.addresses?.[0]?.country || '',
          // ✅ CORRECT: Use 'phone' field from backend
          phone: response.user.phone || ''
        };
        
        setUser(mappedUser);
        setIsAuthenticated(true);
        localStorage.setItem('sheenwear_user', JSON.stringify(mappedUser));
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        if (localStorage.getItem('authToken')) {
          try {
            await mergeGuestCartToApi(
              () => persistGuestBagFromApiExport([]),
              () => guestBagReactSnapshotRef.current
            );
          } catch (mergeErr) {
            console.error('Guest cart merge after login:', mergeErr);
          }
        }
        return { success: true };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('🔧 Login Error:', error);
      
      // Handle different error formats from backend
      let errorMessage = 'Something went wrong. Please try again.';
      let fieldErrors = {};
      
      if (error.message) {
        // Check if it's a backend error response
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.error) {
            // Single error message
            errorMessage = errorData.error;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            // Multiple validation errors
            errorData.errors.forEach(err => {
              if (err.path) {
                fieldErrors[err.path] = err.msg;
              }
            });
            errorMessage = 'Please fix the errors below.';
          }
        } catch (parseError) {
          // If not JSON, use the error message as is
          errorMessage = error.message;
        }
      }
      
      setAuthError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : null
      };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, phone, password, signupOptions = {}) => {
    setLoading(true);
    setAuthError(null);
    try {
      const { phoneVerificationToken, shippingAddress } = signupOptions || {};
      console.log('🔧 Signup Data:', { name, email, phone, password, hasPhoneToken: !!phoneVerificationToken, hasShipping: !!shippingAddress });

      const registerPayload = {
        name,
        email,
        phone,
        password,
        ...(phoneVerificationToken
          ? { phoneVerificationToken: String(phoneVerificationToken).trim() }
          : {}),
        ...(shippingAddress && typeof shippingAddress === 'object'
          ? {
              shippingAddress: {
                addressLine1: String(shippingAddress.addressLine1 || shippingAddress.address || '').trim(),
                city: String(shippingAddress.city || '').trim(),
                state: String(shippingAddress.state || '').trim(),
                postalCode: String(shippingAddress.postalCode || '').trim(),
                country: String(shippingAddress.country || 'Pakistan').trim(),
              },
            }
          : {}),
      };

      const response = await authService.register(registerPayload);
      
      console.log('🔧 Signup Response:', response);
      
      if (response.success && response.user) {
        // ✅ NEW: Handle addresses array in response and phone number mapping
        const mappedUser = {
          ...response.user,
          // Map addresses array back to simple fields for form compatibility
          address: response.user.addresses?.[0]?.addressLine1 || '',
          city: response.user.addresses?.[0]?.city || '',
          country: response.user.addresses?.[0]?.country || '',
          // ✅ CORRECT: Use 'phone' field from backend
          phone: response.user.phone || ''
        };
        
        setUser(mappedUser);
        setIsAuthenticated(true);
        localStorage.setItem('sheenwear_user', JSON.stringify(mappedUser));
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        if (localStorage.getItem('authToken')) {
          try {
            await mergeGuestCartToApi(
              () => persistGuestBagFromApiExport([]),
              () => guestBagReactSnapshotRef.current
            );
          } catch (mergeErr) {
            console.error('Guest cart merge after signup:', mergeErr);
          }
        }

        if (shippingAddress && typeof shippingAddress === 'object') {
          const line1 = String(shippingAddress.addressLine1 || shippingAddress.address || '').trim();
          if (line1) {
            try {
              const addrRes = await authService.updateDetails({
                name,
                phone,
                address: line1,
                city: String(shippingAddress.city || '').trim(),
                state: String(shippingAddress.state || '').trim(),
                postalCode: String(shippingAddress.postalCode || '').trim(),
                country: String(shippingAddress.country || 'Pakistan').trim(),
              });
              if (addrRes.success && addrRes.user) {
                const u = addrRes.user;
                const updatedUser = {
                  ...u,
                  address: u.addresses?.[0]?.addressLine1 || line1,
                  city: u.addresses?.[0]?.city || shippingAddress.city || '',
                  state: u.addresses?.[0]?.state || shippingAddress.state || '',
                  postalCode: u.addresses?.[0]?.postalCode || shippingAddress.postalCode || '',
                  country: u.addresses?.[0]?.country || shippingAddress.country || 'Pakistan',
                  phone: u.phone || phone,
                };
                setUser(updatedUser);
                localStorage.setItem('sheenwear_user', JSON.stringify(updatedUser));
              }
            } catch (addrErr) {
              console.warn('Post-signup delivery address save:', addrErr);
            }
          }
        }
        return { 
          success: true, 
          message: response.message || 'Registration successful!'
        };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('🔧 Signup Error:', error);
      
      // Handle different error formats from backend
      let errorMessage = 'Something went wrong. Please try again.';
      let fieldErrors = {};
      
      if (error.message) {
        // Check if it's a backend error response
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.error) {
            // Single error message
            errorMessage = errorData.error;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            // Multiple validation errors
            errorData.errors.forEach(err => {
              if (err.path) {
                fieldErrors[err.path] = err.msg;
              }
            });
            errorMessage = 'Please fix the errors below.';
          }
        } catch (parseError) {
          // If not JSON, use the error message as is
          errorMessage = error.message;
        }
      }
      
      setAuthError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : null
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await exportAuthenticatedCartToGuestBag();
    } catch (error) {
      console.error('Cart export before logout:', error);
    }
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all authentication data
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(null);
      localStorage.removeItem('sheenwear_user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('userRole');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('userEmail');
    }
  };



  const updateProfile = async (profileData) => {
    setLoading(true);
    setAuthError(null);
    try {
      console.log('🔧 AuthContext: Updating profile with data:', profileData);
      
      const response = await authService.updateDetails(profileData);
      
      console.log('🔧 AuthContext: Profile update response:', response);
      
      if (response.success && response.user) {
        // ✅ ENHANCED: Handle addresses array in response and phone number mapping
        const updatedUser = {
          ...response.user,
          // Map addresses array back to simple fields for form compatibility
          address: response.user.addresses?.[0]?.addressLine1 || response.user.address || '',
          city: response.user.addresses?.[0]?.city || response.user.city || '',
          state: response.user.addresses?.[0]?.state || response.user.state || '',
          postalCode: response.user.addresses?.[0]?.postalCode || response.user.postalCode || '',
          country: response.user.addresses?.[0]?.country || response.user.country || '',
          // ✅ CORRECT: Use 'phone' field from backend
          phone: response.user.phone || ''
        };
        
        console.log('🔧 AuthContext: Mapped updated user:', updatedUser);
        
        setUser(updatedUser);
        localStorage.setItem('sheenwear_user', JSON.stringify(updatedUser));
        return { success: true, message: response.message || 'Profile updated successfully!' };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('🔧 Update Profile Error:', error);
      
      // Handle different error formats from backend
      let errorMessage = 'Something went wrong. Please try again.';
      let fieldErrors = {};
      
      if (error.message) {
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            errorData.errors.forEach(err => {
              if (err.path) {
                fieldErrors[err.path] = err.msg;
              }
            });
            errorMessage = 'Please fix the errors below.';
          }
        } catch (parseError) {
          errorMessage = error.message;
        }
      }
      
      setAuthError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : null
      };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setAuthError(null);
    try {
      console.log('🔧 Change Password Data:', { currentPassword, newPassword });
      
      const response = await authService.changePassword({ currentPassword, newPassword });
      
      console.log('🔧 Change Password Response:', response);
      
      if (response.success) {
        // Update token if provided
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        return { 
          success: true, 
          message: response.message || 'Password updated successfully!'
        };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('🔧 Change Password Error:', error);
      
      // Handle different error formats from backend
      let errorMessage = 'Something went wrong. Please try again.';
      let fieldErrors = {};
      
      if (error.message) {
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.error) {
            // Single error message (e.g., "Current password is incorrect")
            errorMessage = errorData.error;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            // Multiple validation errors
            errorData.errors.forEach(err => {
              if (err.path) {
                fieldErrors[err.path] = err.msg;
              }
            });
            errorMessage = 'Please fix the errors below.';
          }
        } catch (parseError) {
          // If not JSON, use the error message as is
          errorMessage = error.message;
        }
      }
      
      setAuthError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : null
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Get user profile (RECOMMENDED API)
  const getProfile = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required');
      }

      console.log('🔄 AuthContext: Fetching user profile...');

      const response = await fetch(getApiUrl('/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ AuthContext: Profile fetched successfully');
        return data.user;
      } else {
        throw new Error(data.message || 'Failed to fetch profile');
      }
    } catch (error) {
      console.error('❌ AuthContext getProfile error:', error);
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    authError,
    login,
    signup,
    logout,
    updateProfile,
    changePassword,
    getProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 